import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { palette } from '../../utils/palette';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import DetailContainer from '../../components/shared/DetailContainer';

type MakeupMatchingDetailNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

const MakeupMatchingDetailScreen = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future navigation
  const navigation = useNavigation<MakeupMatchingDetailNavigationProp>();
  const { colors } = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for layout
  const insets = useSafeAreaInsets();

  return (
    <DetailContainer style={{ backgroundColor: colors.background }}>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero Section */}
        <LinearGradient
          colors={[palette.softPink, palette.darkPink]}
          style={styles.heroSection}
        >
          <View style={styles.heroContent}>
            <Ionicons
              name="sparkles-outline"
              size={80}
              color="#FFFFFF"
              style={styles.heroIcon}
            />
            <Text style={styles.heroTitle}>Perfect Makeup Colors</Text>
            <Text style={styles.heroSubtitle}>
              Find the perfect makeup colors that complement your natural
              coloring and enhance your beauty
            </Text>
            <View style={styles.comingSoonBadge}>
              <Text style={styles.comingSoonText}>Coming Soon</Text>
            </View>
          </View>
        </LinearGradient>

        {/* Preview Section */}
        <View style={styles.previewSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Personalized Beauty
          </Text>
          <Image
            source={{ uri: 'https://picsum.photos/400/300?random=15' }}
            style={styles.previewImage}
            resizeMode="cover"
          />
          <Text
            style={[styles.previewDescription, { color: colors.secondary }]}
          >
            Discover makeup shades that enhance your natural beauty with
            AI-powered colour matching and brand recommendations.
          </Text>
        </View>

        {/* Features Preview */}
        <View style={[styles.section, { backgroundColor: colors.card }]}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Planned Features
          </Text>
          <View style={styles.featuresList}>
            <View style={styles.featureItem}>
              <Ionicons
                name="color-palette-outline"
                size={24}
                color="#EC4899"
              />
              <Text style={[styles.featureText, { color: colors.text }]}>
                Personalized makeup recommendations
              </Text>
            </View>
            <View style={styles.featureItem}>
              <Ionicons name="storefront-outline" size={24} color="#EC4899" />
              <Text style={[styles.featureText, { color: colors.text }]}>
                Brand-specific suggestions
              </Text>
            </View>
            <View style={styles.featureItem}>
              <Ionicons name="eye-outline" size={24} color="#EC4899" />
              <Text style={[styles.featureText, { color: colors.text }]}>
                Shade matching technology
              </Text>
            </View>
            <View style={styles.featureItem}>
              <Ionicons name="brush-outline" size={24} color="#EC4899" />
              <Text style={[styles.featureText, { color: colors.text }]}>
                Complete look creation
              </Text>
            </View>
          </View>
        </View>

        {/* Notify Section */}
        <View style={styles.notifySection}>
          <LinearGradient
            colors={[palette.softPink, palette.darkPink]}
            style={styles.notifyGradient}
          >
            <Text style={styles.notifyTitle}>Enhance Your Beauty</Text>
            <Text style={styles.notifySubtitle}>
              Get notified when makeup colour matching becomes available
            </Text>
            <TouchableOpacity style={styles.notifyButton}>
              <Text style={styles.notifyButtonText}>Notify Me</Text>
            </TouchableOpacity>
          </LinearGradient>
        </View>
      </ScrollView>
    </DetailContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 10,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  placeholder: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  heroSection: {
    paddingHorizontal: 20,
    paddingVertical: 60,
    alignItems: 'center',
  },
  heroContent: {
    maxWidth: 500,
    alignItems: 'center',
  },
  heroIcon: {
    marginBottom: 20,
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: '800',
    color: palette.white,
    textAlign: 'center',
    marginBottom: 16,
  },
  heroSubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  comingSoonBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  comingSoonText: {
    color: palette.white,
    fontSize: 16,
    fontWeight: '600',
  },
  previewSection: {
    paddingHorizontal: 20,
    paddingVertical: 30,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 24,
  },
  previewImage: {
    width: '100%',
    height: 200,
    borderRadius: 12,
    marginBottom: 16,
  },
  previewDescription: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
  },
  section: {
    paddingHorizontal: 20,
    paddingVertical: 30,
  },
  featuresList: {
    gap: 16,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  featureText: {
    fontSize: 16,
    flex: 1,
  },
  notifySection: {
    paddingHorizontal: 20,
    paddingVertical: 30,
  },
  notifyGradient: {
    borderRadius: 16,
    paddingVertical: 40,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  notifyTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: palette.white,
    textAlign: 'center',
    marginBottom: 8,
  },
  notifySubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginBottom: 24,
  },
  notifyButton: {
    backgroundColor: palette.white,
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
  },
  notifyButtonText: {
    color: palette.softPink,
    fontSize: 16,
    fontWeight: '600',
  },
});

export default MakeupMatchingDetailScreen;
